import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormik, FormikErrors } from 'formik';
import { regexAndErrorMessageMapping } from '@/shared/constants/regex-mapping';
import { ICustomersRegisterRequest } from '@/shared/endpoints/customers/models/request';
import { phoneInput } from '@/shared/helpers/phone-input';
import { validationSchema } from './validations';
import { getDefaultOnboarding } from '../helpers';
// Components
import { Alert, Input } from '@/shared/ui';
import { LayoutActions } from './layout';
import { IParams } from '@/shared/types/helpers';

interface Props {
    onSubmit: (values: ICustomersRegisterRequest) => Promise<void>;
    isLoading?: boolean;
    params: IParams;
}
export interface RegisterForm {
    password: string;
    phoneNumber: string;
    email: string;
    terms: boolean;
}

export const initialValues = {
    email: '',
    phoneNumber: '',
    password: '',
    terms: true,
};

type ErrorsOnLoadToForceUI = {
    [Key in keyof RegisterForm]: boolean;
};

const RegisterFormV2 = ({ onSubmit, isLoading, params }: Props) => {
    const [clicked, setClicked] = useState(false);
    const [elementsThatNeedToPersist, updateElementsThatNeedToPersist] = useState<ErrorsOnLoadToForceUI>({
        email: false,
        phoneNumber: false,
        password: false,
        terms: true
    });

    const titleText = useMemo(() => {
        if (elementsThatNeedToPersist?.email && elementsThatNeedToPersist?.phoneNumber) {
            return 'Update email, phone number, and set password to save your progress';
        } else if (elementsThatNeedToPersist?.email) {
            return 'Update email and set password to save your progress';
        } else if (elementsThatNeedToPersist?.phoneNumber) {
            return 'Update phone number and set password to save your progress';
        } else {
            return 'Set your password to save your progress';
        }
    }, [elementsThatNeedToPersist]);

    const modifyPayload = useCallback((data: RegisterForm) => {
        const { phoneNumber, ...rest } = data;
        const payload = {
            ...rest,
            userOnboarding: getDefaultOnboarding(phoneNumber)
        };

        return payload;
    }, []);

    const formik = useFormik<RegisterForm>({
        initialValues,
        validationSchema,
        validateOnChange: true,
        validateOnBlur: false,
        onSubmit: values => {
            onSubmit(modifyPayload(values));
        }
    });

    const validationRules = useMemo(() => {
        const rules: string[] = [];
        regexAndErrorMessageMapping.forEach(item => {
            const isValid = item.regex.test(formik.values.password);
            if (!isValid) {
                rules.push(item.errorMessage);
            }
        });
        return rules;
    }, [formik.values.password]);

    // setup ...
    const getTypeformData = useCallback(() => {
        const _phoneNumber = typeof params.phone === 'string'
            ? params.phone.replace(/\D/g, '').replace(/\s+/g, '').slice(-10)
            : '';
        const phoneNumber = phoneInput(_phoneNumber);
        const email = typeof params.email === 'string'
            ? params.email.trim().toLowerCase()
            : '';
        const firstName = typeof params.first_name === 'string'
            ? params.first_name.trim()
            : '';
        const lastName = typeof params.last_name === 'string'
            ? params.last_name.trim()
            : '';
        console.log('phoneNumber', phoneNumber);
        console.log('email', email);
        console.log('firstName', firstName);
        console.log('lastName', lastName);
        // set initial values
        formik
            .setValues(prev => {
                return {
                    ...prev,
                    phoneNumber,
                    email,
                    firstName,
                    lastName
                };
            }, true)
            .then(errors => {
                const stateMap: ErrorsOnLoadToForceUI = Object.keys(errors!).reduce((sofar, cur) => {
                    // Type assertion to ensure cur is a key of RegisterForm
                    sofar[cur as keyof RegisterForm] = true;
                    return sofar;
                }, {} as ErrorsOnLoadToForceUI);

                updateElementsThatNeedToPersist(stateMap);
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { password, ...rest } = errors as FormikErrors<RegisterForm>;
                formik.setErrors({
                    ...rest
                });
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleKeyDown = useCallback(
        async (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            setClicked(true);
            if (e.key === 'Enter' && !isLoading && !clicked) {
                await onSubmit(modifyPayload(formik.values));
                setClicked(false);
            }
        },
        [formik, onSubmit, modifyPayload, isLoading, clicked]
    );

    const setFieldsTouched = useCallback(() => {
        if (formik.values.email && !formik.touched.email) {
            formik.setFieldTouched('email', true);
        }
        if (formik.values.phoneNumber && !formik.touched.phoneNumber) {
            formik.setFieldTouched('phoneNumber', true);
        }
    }, [formik]);

    useEffect(() => {
        getTypeformData();
    }, [getTypeformData]);

    useEffect(() => {
        setFieldsTouched();
    }, [setFieldsTouched]);

    const showSetYourPassword = Object.keys(elementsThatNeedToPersist).every(key => {
        if (key === 'password') return true;
        return !elementsThatNeedToPersist[key as keyof ErrorsOnLoadToForceUI];
    });

    return (
        <div data-testid="register-form-v2-container" className="flex flex-col gap-4 lg:gap-7.5">
            <h2 data-testid="register-form-v2-title" className="lg-text-[30px] font-agrandir text-[24px] lg:text-4xl lg:leading-[42px]">{titleText}</h2>
            <div className="flex flex-col items-center gap-5 sm:items-start">
                {elementsThatNeedToPersist?.email && (
                    <Input
                        className="register__email"
                        label="Email"
                        name="email"
                        type="text"
                        placeholder="ex. janed@gmail.com"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={Boolean(formik.errors.email && formik.touched.email)}
                        errorMessage={formik.errors.email}
                        required
                        disabled={false}
                        testId="register-form-email"
                    />
                )}

                {elementsThatNeedToPersist?.phoneNumber && (
                    <Input
                        className="register__phone"
                        label="Phone Number"
                        name="phoneNumber"
                        type="text"
                        placeholder="ex. 945-233-1234"
                        value={phoneInput(formik.values.phoneNumber)}
                        onChange={event => formik.setFieldValue('phoneNumber', phoneInput(event))}
                        onBlur={formik.handleBlur}
                        error={Boolean(formik.errors.phoneNumber && formik.touched.phoneNumber)}
                        errorMessage={formik.errors.phoneNumber}
                        required
                        disabled={false}
                        testId="register-form-phone"
                    />
                )}
                <Input
                    label="Create Password"
                    name="password"
                    type="password"
                    placeholder="•••••••••"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={Boolean(formik.touched.password && formik.errors.password && validationRules.length)}
                    required
                    disabled={false}
                    onKeyDown={handleKeyDown}
                    testId="register-form-create-password"
                />
                {formik.touched.password && formik.errors.password && !!validationRules.length && (
                    <Alert testId="register-form-v2-password-validation-rules" label="Password must contain:" labelClassName="font-dmSans">
                        <ul data-testid="register-form-v2-password-validation-rules-content" className="list-disc pl-4 text-sm lg:text-lg">
                            {validationRules.map((e, index) => (
                                <li data-testid={`register-form-v2-password-validation-rule-${index}`} key={index} className="font-dmSans text-base font-semibold lg:text-lg">
                                    {e}
                                </li>
                            ))}
                        </ul>
                    </Alert>
                )}
                <LayoutActions
                    disabled={isLoading}
                    btnTestId={showSetYourPassword ? 'button-register-form-v2-set-password' : 'button-register-form-v2-continue'}
                    btnText={showSetYourPassword ? 'Set Your Password' : 'Save & Continue'}
                    onClick={() => formik.handleSubmit()}
                />
            </div>
        </div>
    );
};

export default RegisterFormV2;
